// Обрезка текста по длине, не разрезающая суррогатную пару. 11.09.2026
//
// `slice(0, N)` режет по единицам UTF-16. Эмодзи и прочие знаки вне основной
// плоскости Юникода занимают две такие единицы, и граница, пришедшаяся между
// ними, оставляет в строке половину пары. Ломается строка потом — там, куда её
// несут, и по-разному:
//
//  - jsonb её не принимает («Unicode low surrogate must follow a high
//    surrogate»), и запрос падает целиком;
//  - в text драйвер молча кладёт на её место U+FFFD.
//
// Проверено на своей базе на 1d99e5d, прод не сверялся: эмодзи на
// четырёхсотом знаке `why` ронял запись черновика темы уже после вызова
// модели — ни строки в `topic_drafts`, ни записи в журнал, — а название чата,
// название темы и кусок памяти проекта ложились с U+FFFD.
//
// Заслон на входе такой суррогат не видит и видеть не может: ввод был целым,
// половину пары сделал сам сервер.
//
// ГРАНИЦЫ, названные честно:
//
//  - Потолок по-прежнему в единицах UTF-16, а не в знаках: вызывающие сверяют
//    его с `.length`, и счёт знаками удлинил бы строку из эмодзи вдвое.
//  - Кодовая точка — ещё не буква. Эмодзи, склеенный из нескольких (семья,
//    флаг, оттенок кожи), обрывается на составной части: базе это безразлично,
//    страдает вид. Резать по графемам (`Intl.Segmenter`) здесь не взялись.
//  - Одиночный суррогат, пришедший в строке ДО обрезки, остаётся где был:
//    обрезка его не рождает и не лечит. Готовая половина в ответе модели —
//    экранированием внутри JSON — роняла jsonb и без всякой обрезки; её чинит
//    `storableDeep` ниже, на записи ответа модели в jsonb (SMY-222, SMY-331).
//  - `slice` остался там, где базу он не роняет или не режет вовсе (сверено
//    11.09.2026):
//    - `account/profile.ts`, `setName`: ручка отклоняет имя длиннее
//      `NAME_MAX_LENGTH` той же мерой, и служба ничего не обрезает;
//    - `billing/yookassa.ts`: тело ответа ЮKassa в причине отказа
//      (`payments.failure_reason`, text, отдаётся и клиенту) даст U+FFFD;
//    - журналы (`auth/postbox.ts`, `auth/smsaero.ts`) и печать CLI;
//    - экраны клиентов: превью жалобы (`screens/Profile.tsx` веба и iOS) и
//      инициалы на аватаре (`ui/Mark.tsx`, `shell/Shell.tsx`) покажут U+FFFD.

/**
 * Начало строки не длиннее `max` единиц UTF-16 и без половины пары на конце.
 *
 * Разрезанная пара отбрасывается целиком, и строка выходит на единицу короче
 * потолка. Без пары на границе результат тот же, что у `slice(0, max)`.
 */
export function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  const last = text.charCodeAt(max - 1)
  // Последней единицей осталась старшая половина (D800–DBFF): младшая ушла за
  // границу.
  return text.slice(0, last >= 0xd800 && last <= 0xdbff ? max - 1 : max)
}

/**
 * Последние `max` единиц строки, не начиная с младшей половины пары. SMY-494
 *
 * Пара к `truncate`: хвост нужен отрывку классификатора — просьба человека
 * часто стоит после вставленного письма, в самом конце. Граница, пришедшаяся
 * внутрь пары, оставила бы в начале хвоста младшую половину (DC00–DFFF).
 */
export function truncateStart(text: string, max: number): string {
  if (text.length <= max) return text
  const from = text.length - max
  const first = text.charCodeAt(from)
  return text.slice(first >= 0xdc00 && first <= 0xdfff ? from + 1 : from)
}

const REPLACEMENT = String.fromCharCode(0xfffd)

/**
 * Строка без одиночных половин суррогатных пар: каждая заменяется на U+FFFD, целые
 * пары остаются. То же, что `String.prototype.toWellFormed`, которого нет в типах
 * `lib` ES2023 и нет в движках клиентов, — а ядро общее. SMY-222
 *
 * ЗАЧЕМ. Ответ модели разбирается `JSON.parse`, а JSON допускает экранированную
 * одиночную половину пары — и разбор честно отдаёт строку с ней. В jsonb PostgreSQL
 * такую строку не принимает, и запись падает уже после оплаченного вызова модели.
 * В text драйвер кладёт на её место тот же U+FFFD молча — замена здесь ничего не
 * решает за базу, а делает то же самое заранее.
 */
export function wellFormed(text: string): string {
  let out = ''
  let from = 0
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code < 0xd800 || code > 0xdfff) continue
    // Старшая половина (D800–DBFF) с младшей следом — целая пара.
    if (code <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1)
      if (next >= 0xdc00 && next <= 0xdfff) {
        i += 1
        continue
      }
    }
    out += text.slice(from, i) + REPLACEMENT
    from = i + 1
  }
  return from === 0 ? text : out + text.slice(from)
}

/** Нулевой байт. Собирается кодом: экранирование `\u0000` доезжает сюда знаком. */
const NUL = String.fromCharCode(0)

/**
 * Строка, которую примет PostgreSQL: без одиночных половин пар и без нулевого
 * байта. SMY-222, SMY-331
 *
 * Два знака, и оба приходят одной дорогой — из ответа модели. JSON допускает
 * экранирование и половины пары, и нулевого байта, а `JSON.parse` честно отдаёт
 * строку с ними. Дальше падает запись: половину пары не принимает `jsonb`,
 * нулевой байт — ни `jsonb`, ни `text`, и падает уже ПОСЛЕ оплаченного вызова
 * модели.
 *
 * Половина пары заменяется на U+FFFD (в `text` драйвер сделал бы то же молча),
 * нулевой байт удаляется: содержания он не несёт, а видимого следа от него не
 * бывает.
 *
 * ⚠️ ЭТО НЕ ЗАСЛОН ДЛЯ ВВОДА ЧЕЛОВЕКА. Запрос с такими знаками отклоняется с
 * 400 (`apps/api/src/unstorable-text.ts`, SMY-247) — молча чинить то, что
 * прислал человек, нельзя: он не узнает, что его текст переписали. Ответ модели
 * — другое дело: вернуть 400 здесь некому, а платить за вызов уже пришлось.
 */
export function storable(text: string): string {
  const fixed = wellFormed(text)
  return fixed.includes(NUL) ? fixed.split(NUL).join('') : fixed
}

/**
 * `storable` во всех строках значения — и в ключах объектов. Разбирает то, что
 * даёт `JSON.parse`: строки, массивы и простые объекты; остальное возвращает как
 * есть. SMY-222, SMY-331
 *
 * ГДЕ ЗОВЁТСЯ — на записи ответа модели в jsonb: результат запуска
 * (`saveArtifact` в `apps/api/src/runs/tracer.ts`) и черновик темы
 * (`apps/api/src/analytics/drafts.ts`). Других записей чужого текста в jsonb нет:
 * остальные jsonb-колонки пишет персонал и сам сервер.
 *
 * Почему на записи, а не на разборе ответа поставщика. Записи закрывают все пути
 * в базу разом — какой бы разбор и какой бы поставщик ни дал строку, включая
 * поиск и распознавание текста с картинки, которые идут мимо ответа модели. А
 * разбор идёт в том числе кусками потока, и чинить пару по одному куску значило
 * бы решать, где кончается чужая пара. И не слоем всех записей: ввод клиента с
 * такими знаками заслон на входе отклоняет с 400
 * (`apps/api/src/unstorable-text.ts`), а не чинит молча, и здесь его решение не
 * переписывается.
 *
 * ГДЕ ЕЩЁ ЗОВЁТСЯ `storable` — у text-колонок, куда ответ модели ложится без
 * разбора JSON: `messages.body` (`chat/service.ts`), `memory_chunks.text`
 * (`projects/service.ts`), `topics.title` и `summary` (`analytics/topics.ts`),
 * `run_sources.title` и `url` (`runs/tracer.ts`). Список не украшение: починить
 * одну запись и забыть остальные — значит получить молчаливую пропажу там, где
 * сбой ловится в лог (память проекта, лента), и остановленный проход тем там,
 * где не ловится.
 */
export function storableDeep<T>(value: T): T {
  if (typeof value === 'string') return storable(value) as T
  if (Array.isArray(value)) return value.map((item) => storableDeep(item)) as T
  if (value != null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [storable(key), storableDeep(item)]),
    ) as T
  }
  return value
}

/**
 * Текст без управляющих знаков, разрывы строки — одним пробелом. SMY-338
 *
 * Убирается вся категория Cc Юникода: U+0000–U+001F и U+007F–U+009F. Разрывы
 * строки — LF, VT, FF, CR, NEL, U+2028, U+2029 — и табуляция становятся одним
 * пробелом на подряд идущие: текст, вставленный из таблицы или в две строки, —
 * слова через пробел, а не одно слово и не перенос посреди строки. Прочие знаки
 * исчезают.
 *
 * Мест два, и беда у них одна. Имя человека чистится ПЕРЕД базой (`profile.ts`):
 * нулевой байт PostgreSQL в text не принимает, запрос падал, а 500 уносил SQL,
 * имя и id в тело ответа и в журнал. Чужой текст чистится ПЕРЕД терминалом
 * (`ops/console-text.ts`): ESC из обращения или из ответа модели в консоли
 * оператора стирает экран и рисует подделку, а в терминале с OSC 52 кладёт
 * команду ему в буфер обмена.
 *
 * Не трогаются знаки форматирования (Cf) и одиночный суррогат. ZWJ собирает
 * эмодзи из нескольких в один, ZWNJ — часть орфографии ряда письменностей.
 * Знаки направления базу не роняют и терминал не программируют, а вид соседнего
 * текста менять умеют: лечится это изоляцией на выводе (`<bdi>` в вебе), а не
 * очисткой здесь — тот же знак придёт в любом другом поле. Одиночный суррогат не
 * управляющий: его чинит `wellFormed` выше.
 *
 * Строку очистка только укорачивает: проверка длины до неё строже, чем после.
 */
export function withoutControls(raw: string): string {
  let out = ''
  let gap = false
  for (let i = 0; i < raw.length; i++) {
    const code = raw.charCodeAt(i)
    const separator = (code >= 0x09 && code <= 0x0d) || code === 0x85 || code === 0x2028 || code === 0x2029
    const control = code <= 0x1f || (code >= 0x7f && code <= 0x9f)
    if (separator) gap = true
    if (separator || control) continue
    if (gap) out += ' '
    gap = false
    out += raw[i]
  }
  return out
}

/**
 * Отступ между набранным в поле ввода и дописанным туда — новым абзацем.
 * SMY-497 · 26.10.2026
 */
export const FIELD_AFTER_DRAFT = '\n\n'

/**
 * Дописать в поле ввода, не затирая набранное. Одно правило на вернувшуюся
 * попытку (`backInField` в api-client) и заготовку «Изменить»
 * (`withAssumptionEdit`): поле одно, и разойдись правила, одна кнопка затирала
 * бы то, что другая бережёт.
 *
 * Пустое поле — дописанное как есть. Набранное — остаётся, дописанное встаёт
 * после него новым абзацем. Уже стоящее в поле — целиком или последним абзацем
 * — второй раз не дописывается: поле возвращается как было.
 */
export function appendToField(now: string, add: string): string {
  const wanted = add.trim()
  const end = now.trimEnd()
  if (wanted === '' || now.trim() === wanted || end.endsWith(`${FIELD_AFTER_DRAFT}${wanted}`)) return now
  return now.trim() === '' ? add : `${end}${FIELD_AFTER_DRAFT}${add}`
}
